import { useEffect, useState, useCallback } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { AlertTriangle, Plus, Camera, X } from 'lucide-react'
import { Camera as CapCamera, CameraResultType, CameraSource } from '@capacitor/camera'
import { Capacitor } from '@capacitor/core'
import api from '../../services/api'

type Response = 'pass' | 'fail' | 'na' | 'observation'
type ResponseEntry = { response: Response | null; notes: string; photos?: string[] }

const RESPONSE_STYLES: Record<Response, { label: string; colour: string; bg: string }> = {
  pass:        { label: 'Pass',        colour: 'var(--green)',  bg: 'var(--green-light)'  },
  fail:        { label: 'Fail',        colour: 'var(--red)',    bg: 'var(--red-light)'    },
  na:          { label: 'N/A',         colour: 'var(--text-muted)', bg: 'var(--bg)'       },
  observation: { label: 'Observation', colour: 'var(--amber)',  bg: 'var(--amber-light)'  },
}

export default function AuditConduct() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [audit, setAudit] = useState<any>(null)
  const [responses, setResponses] = useState<Record<string, ResponseEntry>>({})
  const [findings, setFindings] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [activeSection, setActiveSection] = useState(0)

  // Photo capture
  const [photoError, setPhotoError] = useState('')

  const takePhoto = async (key: string) => {
    setPhotoError('')
    try {
      let base64: string
      if (Capacitor.isNativePlatform()) {
        // Request permission first
        const perms = await CapCamera.requestPermissions({ permissions: ['camera'] })
        if (perms.camera !== 'granted') {
          setPhotoError('Camera permission denied. Please enable in Settings.')
          return
        }
        const photo = await CapCamera.getPhoto({
          quality: 80,
          allowEditing: false,
          resultType: CameraResultType.Base64,
          source: CameraSource.Camera,
          saveToGallery: false,
          correctOrientation: true,
        })
        base64 = `data:image/jpeg;base64,${photo.base64String}`
      } else {
        base64 = await new Promise((resolve, reject) => {
          const input = document.createElement('input')
          input.type = 'file'
          input.accept = 'image/*'
          input.capture = 'environment'
          input.onchange = () => {
            const file = input.files?.[0]
            if (!file) return reject(new Error('no file'))
            const reader = new FileReader()
            reader.onload = () => resolve(reader.result as string)
            reader.readAsDataURL(file)
          }
          input.click()
        })
      }
      setResponses(prev => {
        const updated = {
          ...prev,
          [key]: {
            ...prev[key],
            response: prev[key]?.response || 'pass' as Response,
            notes: prev[key]?.notes || '',
            photos: [...(prev[key]?.photos || []), base64],
          },
        }
        save(updated)
        return updated
      })
    } catch (err: any) {
      // User cancelled — don't show error
      if (!`${err}`.includes('cancelled') && !`${err}`.includes('canceled') && !`${err}`.includes('No image')) {
        setPhotoError(`Camera error: ${err?.message || err}`)
      }
    }
  }

  const removePhoto = (key: string, idx: number) => {
    const photos = [...(responses[key]?.photos || [])]
    photos.splice(idx, 1)
    const updated = { ...responses, [key]: { ...responses[key], photos } }
    setResponses(updated)
    save(updated)
  }

  // Add finding form
  const [showFinding, setShowFinding] = useState<string | null>(null) // key of item
  const [findingForm, setFindingForm] = useState({ description: '', severity: 'minor', regulation_reference: '', recommended_action: '' })

  const load = async () => {
    try {
      const { data } = await api.get(`/audits/${id}`)
      setAudit(data)
      setFindings(data.findings || [])
      setResponses(data.responses || {})
    } catch {}
    setLoading(false)
  }

  useEffect(() => { load() }, [id])

  const save = useCallback(async (newResponses?: Record<string, any>) => {
    const toSave = newResponses || responses
    setSaving(true)
    try {
      await api.patch(`/audits/${id}`, { responses: toSave, status: 'in_progress' })
    } catch {}
    setSaving(false)
  }, [id, responses])

  const setResponse = (key: string, resp: Response) => {
    const updated = { ...responses, [key]: { ...responses[key], response: resp, notes: responses[key]?.notes || '' } }
    setResponses(updated)
    save(updated)
  }

  const setNotes = (key: string, notes: string) => {
    setResponses(r => ({ ...r, [key]: { ...r[key], notes } }))
  }

  const addFinding = async (key: string, sectionName: string, questionText: string) => {
    if (!findingForm.description.trim()) return
    try {
      await api.post(`/audits/${id}/finding`, {
        section_name: sectionName,
        question_text: questionText,
        response: 'fail',
        severity: findingForm.severity,
        description: findingForm.description,
        regulation_reference: findingForm.regulation_reference || undefined,
        recommended_action: findingForm.recommended_action || undefined,
      })
      await load()
      setShowFinding(null)
      setFindingForm({ description: '', severity: 'minor', regulation_reference: '', recommended_action: '' })
    } catch {}
  }

  const submit = async () => {
    if (!confirm('Submit this audit? This will calculate the score and generate an AI summary.')) return
    setSubmitting(true)
    try {
      await save()
      await api.post(`/audits/${id}/submit`, {})
      navigate(`/audits/${id}`)
    } catch {}
    setSubmitting(false)
  }

  if (loading || !audit) return <div className="text-center py-16" style={{ color: 'var(--text-muted)' }}>Loading audit...</div>

  const sections: any[] = audit.template_sections || []
  const hasTemplate = sections.length > 0

  // Calculate progress
  const totalQ = sections.reduce((n: number, s: any) => n + (s.items?.length || 0), 0)
  const answeredQ = Object.values(responses).filter((r: any) => r?.response || (r?.photos && r.photos.length > 0)).length
  const pct = totalQ > 0 ? Math.round((answeredQ / totalQ) * 100) : 0

  return (
    <div className="max-w-3xl mx-auto space-y-4 pb-24">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <Link to="/audits" className="text-sm" style={{ color: 'var(--accent)' }}>← Audits</Link>
          <h1 className="text-xl font-bold mt-1" style={{ color: 'var(--text)' }}>{audit.audit_number}</h1>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
            {audit.audit_type?.replace('_', ' ').toUpperCase()} · {audit.site_name || 'All sites'} · {audit.auditor_name}
          </p>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-2xl font-bold" style={{ color: pct === 100 ? 'var(--green)' : 'var(--text)' }}>{pct}%</p>
          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{answeredQ}/{totalQ} answered</p>
          {saving && <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Saving…</p>}
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--border)' }}>
        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: pct === 100 ? 'var(--green)' : 'var(--accent)' }} />
      </div>

      {/* Section tabs (if template) */}
      {hasTemplate && sections.length > 1 && (
        <div className="flex gap-1 overflow-x-auto pb-1">
          {sections.map((sec: any, i: number) => {
            const answered = (sec.items || []).filter((_: any, qi: number) => responses[`${i}_${qi}`]?.response).length
            const total = sec.items?.length || 0
            return (
              <button
                key={i}
                onClick={() => setActiveSection(i)}
                className="flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all"
                style={{
                  background: activeSection === i ? 'var(--accent)' : 'var(--bg)',
                  color: activeSection === i ? 'white' : 'var(--text-secondary)',
                  border: `1px solid ${activeSection === i ? 'var(--accent)' : 'var(--border)'}`,
                }}
              >
                {sec.title} ({answered}/{total})
              </button>
            )
          })}
        </div>
      )}

      {/* Findings summary */}
      {findings.length > 0 && (
        <div className="card p-4">
          <p className="text-sm font-semibold mb-2" style={{ color: 'var(--text)' }}>Findings ({findings.length})</p>
          <div className="space-y-1">
            {findings.map(f => (
              <div key={f.id} className="flex items-start gap-2 text-xs">
                <span className={`badge ${f.severity === 'critical' ? 'badge-critical' : f.severity === 'major' ? 'badge-high' : 'badge-medium'} flex-shrink-0`}>{f.severity}</span>
                <span style={{ color: 'var(--text)' }}>{f.description}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Template-based questions */}
      {hasTemplate ? (
        <div className="card space-y-4">
          <h2 className="font-semibold" style={{ color: 'var(--text)' }}>{sections[activeSection]?.title}</h2>
          {(sections[activeSection]?.items || []).map((item: any, qi: number) => {
            const key = `${activeSection}_${qi}`
            const r = responses[key]
            const itemFindings = findings.filter(f => f.section_name === sections[activeSection]?.title && f.question_text === item.question)

            return (
              <div key={key} className="space-y-2 pb-4" style={{ borderBottom: '1px solid var(--border)' }}>
                <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>
                  <span className="font-mono text-xs mr-2" style={{ color: 'var(--text-muted)' }}>{qi + 1}.</span>
                  {item.type === 'photo' && <Camera size={13} style={{ display: 'inline', marginRight: 5, color: 'var(--accent)' }} />}
                  {item.question}
                </p>
                {item.requirement && <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{item.requirement}</p>}
                {item.regulation_ref && <p className="text-xs" style={{ color: 'var(--accent)' }}>Ref: {item.regulation_ref}</p>}

                {item.type === 'photo' ? (
                  /* Photo capture UI */
                  <div className="space-y-2">
                    {photoError && (
                      <p className="text-xs p-2 rounded" style={{ background: 'var(--red-light)', color: 'var(--red)' }}>{photoError}</p>
                    )}
                    {(r?.photos || []).length > 0 && (
                      <div className="flex gap-2 flex-wrap">
                        {(r.photos as string[]).map((src, idx) => (
                          <div key={idx} style={{ position: 'relative', width: 80, height: 80 }}>
                            <img src={src} alt="" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--border)' }} />
                            <button
                              onClick={() => removePhoto(key, idx)}
                              style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%', background: 'var(--red)', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 }}
                            >
                              <X size={11} color="white" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    <button
                      onClick={() => takePhoto(key)}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold border transition-all"
                      style={{ background: 'var(--white)', color: 'var(--accent)', borderColor: 'var(--accent)', cursor: 'pointer' }}
                    >
                      <Camera size={15} />
                      {(r?.photos || []).length > 0 ? 'Add Another Photo' : 'Take Photo'}
                    </button>
                    {(r?.photos || []).length === 0 && (
                      <button
                        onClick={() => {
                          const updated = { ...responses, [key]: { ...responses[key], response: 'na' as Response, notes: responses[key]?.notes || '', photos: [] } }
                          setResponses(updated); save(updated)
                        }}
                        className="text-xs"
                        style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}
                      >
                        Mark as N/A
                      </button>
                    )}
                    {r?.response === 'na' && (
                      <span className="text-xs px-2 py-1 rounded" style={{ background: 'var(--bg)', color: 'var(--text-muted)' }}>N/A</span>
                    )}
                  </div>
                ) : (
                  /* Standard response buttons */
                  <>
                    <div className="flex gap-2 flex-wrap">
                      {(['pass', 'fail', 'na', 'observation'] as Response[]).map(resp => (
                        <button
                          key={resp}
                          onClick={() => setResponse(key, resp)}
                          className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all border"
                          style={{
                            background: r?.response === resp ? RESPONSE_STYLES[resp].bg : 'var(--white)',
                            color: r?.response === resp ? RESPONSE_STYLES[resp].colour : 'var(--text-secondary)',
                            borderColor: r?.response === resp ? RESPONSE_STYLES[resp].colour : 'var(--border)',
                          }}
                        >
                          {RESPONSE_STYLES[resp].label}
                        </button>
                      ))}
                    </div>

                    {/* Notes (shown when not pass/na) */}
                    {r?.response && r.response !== 'pass' && r.response !== 'na' && (
                      <textarea
                        rows={2}
                        placeholder="Notes..."
                        value={r.notes || ''}
                        onChange={e => setNotes(key, e.target.value)}
                        onBlur={() => save()}
                      />
                    )}
                  </>
                )}

                {/* Add finding button for fail/observation */}
                {(r?.response === 'fail' || r?.response === 'observation') && (
                  <div>
                    <button
                      onClick={() => setShowFinding(showFinding === key ? null : key)}
                      className="flex items-center gap-1 text-xs"
                      style={{ color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer' }}
                    >
                      <Plus size={13} /> Log Finding
                    </button>
                    {showFinding === key && (
                      <div className="mt-2 space-y-2 p-3 rounded-lg" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-xs font-semibold mb-1 block" style={{ color: 'var(--text-secondary)' }}>Description *</label>
                            <textarea rows={2} placeholder="Finding description..." value={findingForm.description} onChange={e => setFindingForm(f => ({ ...f, description: e.target.value }))} />
                          </div>
                          <div>
                            <label className="text-xs font-semibold mb-1 block" style={{ color: 'var(--text-secondary)' }}>Severity</label>
                            <select value={findingForm.severity} onChange={e => setFindingForm(f => ({ ...f, severity: e.target.value }))}>
                              <option value="observation">Observation</option>
                              <option value="minor">Minor</option>
                              <option value="major">Major</option>
                              <option value="critical">Critical</option>
                            </select>
                            <label className="text-xs font-semibold mt-2 mb-1 block" style={{ color: 'var(--text-secondary)' }}>Regulation Ref</label>
                            <input placeholder="e.g. WHS Reg 2017 s.35" value={findingForm.regulation_reference} onChange={e => setFindingForm(f => ({ ...f, regulation_reference: e.target.value }))} />
                          </div>
                        </div>
                        <input placeholder="Recommended action..." value={findingForm.recommended_action} onChange={e => setFindingForm(f => ({ ...f, recommended_action: e.target.value }))} />
                        <div className="flex gap-2">
                          <button onClick={() => addFinding(key, sections[activeSection]?.title, item.question)} className="btn btn-primary text-xs py-1.5">Add Finding</button>
                          <button onClick={() => setShowFinding(null)} className="btn btn-secondary text-xs py-1.5">Cancel</button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {itemFindings.length > 0 && (
                  <div className="space-y-1">
                    {itemFindings.map(f => (
                      <div key={f.id} className="flex items-center gap-2 text-xs p-2 rounded" style={{ background: 'var(--red-light)' }}>
                        <AlertTriangle size={12} style={{ color: 'var(--red)', flexShrink: 0 }} />
                        <span style={{ color: 'var(--red)' }}>{f.description}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      ) : (
        // Blank audit — just findings log
        <div className="card space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold" style={{ color: 'var(--text)' }}>Findings</h2>
            <button onClick={() => setShowFinding('manual')} className="btn btn-secondary text-sm flex items-center gap-1.5"><Plus size={14} /> Add Finding</button>
          </div>
          {showFinding === 'manual' && (
            <div className="space-y-3 p-4 rounded-xl" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold mb-1 block" style={{ color: 'var(--text-secondary)' }}>Description *</label>
                  <textarea rows={3} value={findingForm.description} onChange={e => setFindingForm(f => ({ ...f, description: e.target.value }))} />
                </div>
                <div className="space-y-2">
                  <div>
                    <label className="text-xs font-semibold mb-1 block" style={{ color: 'var(--text-secondary)' }}>Severity</label>
                    <select value={findingForm.severity} onChange={e => setFindingForm(f => ({ ...f, severity: e.target.value }))}>
                      <option value="observation">Observation</option>
                      <option value="minor">Minor</option>
                      <option value="major">Major</option>
                      <option value="critical">Critical</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-semibold mb-1 block" style={{ color: 'var(--text-secondary)' }}>Regulation Ref</label>
                    <input value={findingForm.regulation_reference} onChange={e => setFindingForm(f => ({ ...f, regulation_reference: e.target.value }))} />
                  </div>
                </div>
              </div>
              <input placeholder="Recommended action..." value={findingForm.recommended_action} onChange={e => setFindingForm(f => ({ ...f, recommended_action: e.target.value }))} />
              <div className="flex gap-2">
                <button onClick={() => addFinding('manual', '', '')} className="btn btn-primary text-sm">Add Finding</button>
                <button onClick={() => setShowFinding(null)} className="btn btn-secondary text-sm">Cancel</button>
              </div>
            </div>
          )}
          {findings.length === 0 && <p className="text-sm text-center py-6" style={{ color: 'var(--text-muted)' }}>No findings logged yet</p>}
          {findings.map(f => (
            <div key={f.id} className="p-3 rounded-lg" style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
              <div className="flex items-center gap-2 mb-1">
                <span className={`badge ${f.severity === 'critical' ? 'badge-critical' : f.severity === 'major' ? 'badge-high' : 'badge-medium'}`}>{f.severity}</span>
                {f.regulation_reference && <span className="text-xs" style={{ color: 'var(--accent)' }}>{f.regulation_reference}</span>}
              </div>
              <p className="text-sm" style={{ color: 'var(--text)' }}>{f.description}</p>
              {f.recommended_action && <p className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>→ {f.recommended_action}</p>}
            </div>
          ))}
        </div>
      )}

      {/* Submit bar */}
      <div className="fixed bottom-0 left-0 right-0 p-4 border-t flex items-center justify-between gap-4" style={{ background: 'var(--white)', borderColor: 'var(--border)' }}>
        <div className="text-sm" style={{ color: 'var(--text-muted)' }}>
          {hasTemplate ? `${answeredQ}/${totalQ} questions answered` : `${findings.length} findings logged`}
        </div>
        <div className="flex gap-3">
          <button onClick={() => save()} disabled={saving} className="btn btn-secondary">{saving ? 'Saving...' : 'Save Progress'}</button>
          <button onClick={submit} disabled={submitting} className="btn btn-primary">
            {submitting ? 'Submitting...' : 'Submit Audit'}
          </button>
        </div>
      </div>
    </div>
  )
}
